/** Bounded offline message window persisted across cold starts. */

export const PERSIST_MESSAGE_COUNT = 50;
export const PERSIST_THREAD_COUNT = 10;

export function isDmMessageThreadQueryKey(queryKey) {
  return (
    Array.isArray(queryKey) &&
    queryKey.length === 4 &&
    queryKey[0] === 'chats' &&
    queryKey[1] === 'dm' &&
    queryKey[3] === 'messages'
  );
}

export function isGroupMessageThreadQueryKey(queryKey) {
  return (
    Array.isArray(queryKey) &&
    queryKey.length === 3 &&
    queryKey[0] === 'groups' &&
    queryKey[2] === 'messages'
  );
}

export function isMessageThreadQueryKey(queryKey) {
  return isDmMessageThreadQueryKey(queryKey) || isGroupMessageThreadQueryKey(queryKey);
}

/** Keep the most recent messages by created_at (ascending display order). */
export function trimMessagesForPersist(messages, limit = PERSIST_MESSAGE_COUNT) {
  if (!Array.isArray(messages) || messages.length <= limit) return messages;
  return [...messages]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .slice(-limit);
}

function queryActivityTimestamp(query) {
  const updatedAt = query?.state?.dataUpdatedAt;
  if (typeof updatedAt === 'number' && updatedAt > 0) return updatedAt;

  const messages = query?.state?.data;
  if (!Array.isArray(messages) || messages.length === 0) return 0;

  return messages.reduce((max, row) => {
    const ts = row?.created_at ? new Date(row.created_at).getTime() : 0;
    return ts > max ? ts : max;
  }, 0);
}

/**
 * Keep ~PERSIST_THREAD_COUNT most recently active threads, each trimmed to ~PERSIST_MESSAGE_COUNT rows.
 * Applied at serialize time so shouldDehydrateQuery can allow message queries through.
 */
export function boundMessageQueriesInDehydratedClient(persistedClient) {
  if (!persistedClient?.clientState?.queries) return persistedClient;

  const queries = persistedClient.clientState.queries;
  const messageQueries = [];
  const otherQueries = [];

  queries.forEach((query, index) => {
    if (isMessageThreadQueryKey(query.queryKey)) {
      messageQueries.push({ query, index });
    } else {
      otherQueries.push(query);
    }
  });

  if (messageQueries.length === 0) {
    return persistedClient;
  }

  const ranked = messageQueries
    .map(({ query, index }) => ({
      index,
      query,
      activity: queryActivityTimestamp(query),
    }))
    .sort((a, b) => b.activity - a.activity);

  const keepSet = new Set(ranked.slice(0, PERSIST_THREAD_COUNT).map((entry) => entry.index));

  const boundedMessageQueries = messageQueries
    .map(({ query, index }) => {
      if (!keepSet.has(index)) return null;

      const data = query.state?.data;
      if (!Array.isArray(data)) return query;

      return {
        ...query,
        state: {
          ...query.state,
          data: trimMessagesForPersist(data),
        },
      };
    })
    .filter(Boolean);

  return {
    ...persistedClient,
    clientState: {
      ...persistedClient.clientState,
      queries: [...otherQueries, ...boundedMessageQueries],
    },
  };
}

/** Mark a thread as recently viewed so it ranks in the persist window. */
export function touchMessageThread(queryClient, queryKey) {
  if (!queryClient || !isMessageThreadQueryKey(queryKey)) return;
  const query = queryClient.getQueryCache().find({ queryKey });
  const now = Date.now();
  if (query?.state?.dataUpdatedAt && now - query.state.dataUpdatedAt < 1000) return;
  const existing = queryClient.getQueryData(queryKey);
  queryClient.setQueryData(queryKey, existing ?? [], { updatedAt: now });
}

/** Rough payload estimate for dev reporting (10×50 messages). */
export function estimateBoundedMessagePersistBytes({
  threadCount = PERSIST_THREAD_COUNT,
  messagesPerThread = PERSIST_MESSAGE_COUNT,
} = {}) {
  const sampleMessage = {
    id: '00000000-0000-4000-8000-000000000000',
    user_id: '00000000-0000-4000-8000-000000000001',
    recipient_user_id: '00000000-0000-4000-8000-000000000002',
    group_id: null,
    content: 'See you at the main stage around 9 — saved offline for the group.',
    message_type: 'user',
    conversation_type: 'dm',
    created_at: '2026-07-24T22:00:00.000Z',
    profile: {
      id: '00000000-0000-4000-8000-000000000001',
      display_name: 'Sam',
      profile_photo_url: 'https://example.com/photo.jpg',
    },
  };

  const queries = Array.from({ length: threadCount }, (_, i) => ({
    queryKey: ['chats', 'dm', `conv-${i}`, 'messages'],
    state: {
      data: Array.from({ length: messagesPerThread }, () => sampleMessage),
      dataUpdatedAt: Date.now(),
    },
  }));

  const payload = {
    clientState: { queries, mutations: [] },
  };

  return new Blob([JSON.stringify(payload)]).size;
}
